"use client";

import React from "react";
import { mergeTheme } from "../utils/theme";
import { CenterContent } from "./center-content";
import { SideAction } from "./side-action";
import { defaultTheme } from "./themes";
import { NavHeaderProps } from "./types";

/**
 * NavHeader Component
 *
 * A flexible navigation header component with support for logos, titles, and action buttons.
 * Features absolute positioning for side actions to prevent layout shifts and includes
 * iOS safe area padding support.
 *
 * @param showLogo - Whether to display the logo in the center
 * @param showTitle - Whether to display the title in the center
 * @param title - Title text to display
 * @param subtitle - Subtitle text to display below the header
 * @param logo - Custom logo component to render
 * @param leftAction - Configuration for left side action button
 * @param rightAction - Configuration for right side action button
 * @param theme - Theme overrides for styling
 * @param className - Additional CSS classes
 * @param TypographyEl - Custom typography component for title rendering
 * @param SubtitleEl - Custom typography component for subtitle rendering
 */
export const NavHeader: React.FC<NavHeaderProps> = ({
  showLogo = false,
  showTitle = false,
  title,
  subtitle,
  logo,
  leftAction,
  rightAction,
  theme,
  className = "",
  TypographyEl,
  SubtitleEl,
}) => {
  const mergedTheme = mergeTheme(defaultTheme, theme);

  // Build className from theme
  const themeClasses = mergedTheme.container || "";

  // Combine theme classes with custom className
  const finalClassName = `flex flex-col ${themeClasses} ${className}`.trim();

  // Add safe area padding for iOS devices
  const safeAreaStyle: React.CSSProperties = {
    paddingTop: "calc(var(--felt-safe-area-top, 0px) + 1rem)",
  };

  return (
    <header className={finalClassName} style={safeAreaStyle}>
      {/* Main header row with logo and actions */}
      <div className="flex items-center relative">
        {/* Left and right actions positioned absolutely with z-index */}
        <div className="absolute left-0 flex items-center z-10">
          <SideAction action={leftAction} theme={mergedTheme} />
        </div>

        {/* Center content takes full width and centers its content */}
        <div className="flex-1 flex items-center justify-center">
          <CenterContent
            showLogo={showLogo}
            showTitle={showTitle}
            title={title}
            logo={logo}
            theme={mergedTheme}
            TypographyEl={TypographyEl}
          />
        </div>

        {/* Right action positioned absolutely with z-index */}
        <div className="absolute right-0 flex items-center z-10">
          <SideAction action={rightAction} theme={mergedTheme} />
        </div>
      </div>

      {/* Subtitle row (if provided) */}
      {subtitle && (
        <div className={mergedTheme.subtitleContainer || ""}>
          {SubtitleEl ? (
            <SubtitleEl className={mergedTheme.subtitleText || ""}>
              {subtitle}
            </SubtitleEl>
          ) : (
            <div className={mergedTheme.subtitleText || ""}>{subtitle}</div>
          )}
        </div>
      )}
    </header>
  );
};

// Export everything for extensibility
export { createButton } from "./button-factory";
export * from "./themes";
export * from "./types";
